import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { ListingFilter } from '@df/catalog-ui/listing/listing';
import { Model } from '@df/catalog/model';
import compact from 'lodash-es/compact';
import findIndex from 'lodash-es/findIndex';

export const PRIMARY_TAG_FILTER_TYPE = 'primary-tag';

@ListingFilter({
    filterType: PRIMARY_TAG_FILTER_TYPE
})
export class FilterPrimaryTag<T extends Model> extends Filter<T, string[], string> {
    /**
     * Initial selection
     */
    protected override _selection: string[] = [];

    /**
     * Query filter alway has matches
     */
    readonly hasAnyMatches = true;

    /**
     * Resets selection
     */
    reset() {
        if (this.hasSelection) {
            this._selection = [];
            return this.next();
        }
    }

    /**
     * Selects option. Will unselect any other options if requested so.
     */
    select(option: string, unselectOther: boolean = false): void {
        if (unselectOther) {
            this._selection = [option];
        } else {
            if (!this.isSelected(option)) {
                this._selection.push(option);
            }
        }
        this.next();
    }

    /**
     * Unselects option.
     */
    unselect(option: string): void {
        this._selection = this._selection.filter(selectedOption => selectedOption !== option);
        this.next();
    }

    /**
     * Toggles option. Will unselect any other options if requested so (and selected).
     */
    toggle(option: string, unselectOther: boolean = false): void {
        if (this.isSelected(option)) {
            this.unselect(option);
        } else {
            this.select(option, unselectOther);
        }
    }

    /**
     * Checks if option is currently selected
     */
    isSelected(option: string): boolean {
        return this._selection.includes(option);
    }

    /**
     * Applies filter to models collection
     * @param  {T[]} collection Input collection to filter
     * @return {T[]}            Filtered collection
     */
    applyFilter(collection: T[]): T[] {
        // do not apply if no bounds set
        if (!this.hasSelection) {
            return collection;
        }

        return collection.filter(
            model =>
                findIndex(
                    this._selection,
                    selection =>
                        model.data &&
                        model.data['typeData'] &&
                        model.data['typeData']['primary_tag'] &&
                        selection === model.data['typeData']['primary_tag']
                ) !== -1
        );
    }

    /**
     * Checks if filter is applied
     * @return {boolean}
     */
    get hasSelection(): boolean {
        return !!this._selection.length;
    }

    getState(): string[] {
        return <string[]>compact(this._selection);
    }

    setState(state: string[]) {
        if (!state || !Array.isArray(state)) {
            return;
        }
        this._selection = state.map(i => i.toString());
    }
}
